import { FC, useMemo } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { PolygonPlaneMeshProps } from '@editor/interfaces/canvas'
import { useAppSelector } from '@editor/store/app'
import { TransformControls } from '@react-three/drei'
import { isTablet } from 'react-device-detect'
import { Matrix4, Quaternion, Vector3 } from 'three'

import { PolygonPlaneMeshV2 } from './PolygonPlaneMeshV2'
import { useTransforms } from './hooks/useTransforms'

/**
 * Wrapper around PolygonPlaneMeshV2 to make it transformable with TransformControls.
 *
 * Only one can be used/rendered at the same time as various flags and events does not
 * have any identifications to differentiate multiple instances.
 *
 * @param props Same as Polygon plane mesh props.
 */
const PolygonPlaneMeshTransformable: FC<PolygonPlaneMeshProps> = (props) => {
  const { vertexAnchors } = props
  const { transformControlsRef, controlledGroupRef, isAnchorDown, handleMouseUp, handleMouseDown } =
    useTransforms(props)

  // Desctructure props
  const { polygon, offsetTransformation, transformControls } = props

  // Store
  const currentTransformType = useAppSelector((state) => state.editor.currentTransformType)
  const isAdjustingVertex = useAppSelector((state) => state.toolUtil.core.isAdjustingVertex)

  // Transformation pivot point. [0,0,0] should never happen as polygon always comes with center defined
  const pivot: PointArray = useMemo(
    () => transformControls?.pivotPoint || polygon.center || [0, 0, 0],
    [transformControls?.pivotPoint, polygon.center],
  )

  /**
   * Setup offset matrix.
   * Use whatever is provided, or identity if none.
   */
  const offsetMatrix = useMemo(() => {
    if (offsetTransformation instanceof Matrix4) return offsetTransformation
    return offsetTransformation ? new Matrix4().fromArray(offsetTransformation) : new Matrix4().identity()
  }, [offsetTransformation])

  /**
   * Get position from offset matrix.
   */
  const { position: offsetPosition, quaternion: offsetQuaternion } = useMemo(() => {
    if (!offsetTransformation) return { position: new Vector3(), quaternion: new Quaternion(), matrix: new Matrix4() }

    const pos = new Vector3()
    const quat = new Quaternion()

    offsetMatrix.decompose(pos, quat, new Vector3())

    return {
      position: pos,
      quaternion: quat,
      matrix: offsetMatrix,
    }
  }, [offsetTransformation, offsetMatrix])

  return (
    <TransformControls
      ref={transformControlsRef}
      size={isTablet ? 1 : 0.75}
      space="local"
      mode={currentTransformType}
      enabled={(transformControls?.enabled ?? true) && !isAdjustingVertex && !!currentTransformType}
      position={offsetPosition}
      quaternion={offsetQuaternion}
      onMouseUp={handleMouseUp}
      onMouseDown={handleMouseDown}
    >
      <group ref={controlledGroupRef} name="PolygonPlaneMeshTransformableGroup">
        <PolygonPlaneMeshV2
          {...props}
          origin={pivot}
          offsetTransformation={new Matrix4().identity()}
          vertexAnchors={
            vertexAnchors
              ? {
                  ...vertexAnchors,
                  onDown: (anchorIndex, event) => {
                    // If vertex was modified while user is hovering over transform controls,
                    // transform controll will think the mouse operation is on itself
                    // but since this is fired first, we set the flag to indicate anchor is
                    // being pressed down so transform controls can ignore the mouse events.
                    isAnchorDown.current = true
                    vertexAnchors.onDown?.(anchorIndex, event)
                  },
                  onUpdated(points) {
                    isAnchorDown.current = false
                    vertexAnchors.onUpdated?.(points)
                  },
                }
              : undefined
          }
        />
      </group>
    </TransformControls>
  )
}

export default PolygonPlaneMeshTransformable
